import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import { User, UserEducation } from "../Profile";
import EducationDisplay from "./EducationDisplay";
import { MonthPicker } from "../MonthPicker";
import { toast } from "react-toastify";

type EducationProps = {
  handleClick: () => void;
};

export const Education: React.FC<EducationProps> = ({ handleClick }) => {
  const dispatch = useDispatch();

  const [degree, setDegree] = useState("");
  const [gpa, setGpa] = useState("");
  const [school, setSchool] = useState("");
  const [minor, setMinor] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [description, setDescription] = useState("");
  const [failedSubmit, setFailedSubmit] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = React.useState(false);

  const user = useSelector((state: AppState) => state.user); // get the current user object from the Redux store
  const [numEd, setNumEd] = useState(
    user.projects == undefined ? 0 : user.projects.length
  );
  const [education, setEducation] = useState<UserEducation[]>(
    user.educations == undefined ? [] : user.educations
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const updateState = (eds: UserEducation[]) => {
    const updatedUser: User = {
      ...user, // create a new object with all existing properties of the user object
      educations: eds,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  const updateUserData = () => {
    if (degree.length === 0 || school.length === 0) {
      toast.error("Please complete all required fields.");
      setFailedSubmit(true);
      return;
    }

    setFailedSubmit(false);

    const ed: UserEducation = {
      degree: degree,
      school: school,
      start_date: startTime,
      end_date: endTime,
      minor: minor,
      gpa: gpa,
      notes: description,
      id: numEd,
    };

    setNumEd(numEd + 1);

    const eds = education.concat(ed);

    setEducation(eds);

    updateState(eds);

    setDegree("");
    setGpa("");
    setSchool("");
    setMinor("");
    setDescription("");
  };

  const removeEducation = (index: number) => {
    const eds = education.filter((ed) => ed.id != index);
    setEducation(eds);
    updateState(eds);
  };

  const handleDegreeInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setDegree(event.currentTarget.value);
  };

  const handleGpaInput = (event: React.FormEvent<HTMLInputElement>) => {
    setGpa(event.currentTarget.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isNumber = /[0-9]/.test(event.key);
    const isPeriod = event.key === "." && !String(gpa).includes(".");

    if (!isNumber && !isPeriod) {
      event.preventDefault();
    }
  };

  const handleSchoolInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setSchool(event.currentTarget.value);
  };

  const handleMinorInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setMinor(event.currentTarget.value);
  };

  const handleDescription = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setDescription(event.currentTarget.value);
  };

  return (
    <div>
      <p className="text-light mb-10 pl-4 text-2xl font-bold lg:pl-0 lg:text-3xl xl:text-4xl">
        <span className="text-orange">Degrees</span> and{" "}
        <span className="text-blue">Certifications</span>
      </p>

      {isOverlayOpen && (
        <div className="overlay">
          <div className="agreement-content w-1/2">
            <h2 className="text-center font-code md:text-lg lg:text-xl xl:text-2xl ">
              You have a partial degree/certification entry. Are you sure you
              want to move on?
            </h2>

            <div className="flex w-full flex-row pt-4 lg:pt-6">
              <button
                className="start-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => handleClick()}
              >
                Yes, I&apos;m sure
              </button>
              <button
                className="login-button mx-auto px-4 py-2 font-bold md:text-lg lg:text-xl xl:text-2xl"
                onClick={() => setIsOverlayOpen(false)}
              >
                Nevermind
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col space-x-10 md:flex-row">
        <div className="ml-auto hidden h-full w-full overflow-y-scroll rounded-md bg-[#1E1E1E] p-2 md:block md:max-h-[40rem] md:w-1/4">
          {education.map((ed, index) => {
            const deleteIndex = () => {
              removeEducation(ed.id);
            };

            return (
              <EducationDisplay
                key={index}
                degree={ed.degree}
                school={ed.school}
                start_time={ed.start_date}
                end_time={ed.end_date}
                index={index + 1}
                deleteRow={deleteIndex}
              />
            );
          })}

          {education.length === 0 && (
            <h1 className="mt-20 h-32 font-code text-lg font-bold text-[#9CA3AF] lg:text-xl xl:text-2xl">
              No degrees added...
            </h1>
          )}
        </div>

        <div className="w-4/5">
          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                DEGREE
                <span className="text-blue text-base lg:text-lg xl:text-xl">
                  {" "}
                  (or certification)
                </span>{" "}
                <span className="text-red-500">*</span>
              </p>
              <textarea
                value={degree}
                onInput={handleDegreeInput}
                rows={1}
                className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                  failedSubmit ? "border-2 border-red-600" : ""
                }`}
                ref={textareaRef}
                placeholder="B.S. in Computer Science"
              />
            </div>
            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-blue pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                GPA
                <span className="text-orange text-base lg:text-lg xl:text-xl">
                  {" "}
                  (if applicable)
                </span>
              </p>
              <input
                value={gpa}
                onChange={handleGpaInput}
                onKeyPress={handleKeyPress}
                className="answer-box mt-3 max-h-16 w-full overflow-y-hidden p-5 text-2xl"
                placeholder="3.43"
              />
            </div>
          </div>
          <div className="mb-5 flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-blue pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                SCHOOL
                <span className="text-orange text-base lg:text-lg xl:text-xl">
                  {" "}
                  (or institution)
                </span>{" "}
                <span className="text-red-500">*</span>
              </p>
              <textarea
                value={school}
                onInput={handleSchoolInput}
                rows={1}
                className={`answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl ${
                  failedSubmit ? "border-2 border-red-600" : ""
                }`}
                ref={textareaRef}
                placeholder="Olympus Mons University"
              />
            </div>
            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                MINOR
                <span className="text-blue text-base lg:text-lg xl:text-xl">
                  {" "}
                  (if applicable)
                </span>
              </p>
              <textarea
                value={minor}
                onInput={handleMinorInput}
                rows={1}
                className="answer-box mt-3 max-h-min w-full overflow-y-hidden p-5 text-2xl"
                ref={textareaRef}
                placeholder="Jazz Music"
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row">
            <div className="inline-block w-full md:w-1/2">
              <p className="text-orange pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                START DATE
              </p>
              <MonthPicker handleInput={setStartTime} disabled={false} />
            </div>
            <div className="ml-auto inline-block w-full md:w-2/5">
              <p className="text-blue pl-3 text-xl font-bold lg:text-2xl xl:text-3xl">
                END DATE
              </p>
              <MonthPicker handleInput={setEndTime} disabled={false} />
            </div>
          </div>

          <div className="flex flex-col md:flex-row">
            <textarea
              value={description}
              rows={2}
              onInput={handleDescription}
              className="answer-box mt-3 w-full overflow-y-hidden p-5 text-2xl"
              ref={textareaRef}
              placeholder="Additional notes about activities or other impressive feats, if you like."
            />
          </div>

          <div className="flex flex-row space-x-10">
            <button
              className="start-button mt-5 px-10 py-2 text-lg font-bold md:py-4 md:text-xl lg:px-16 lg:text-3xl"
              onClick={updateUserData}
            >
              Add
            </button>
            <button
              className="login-button mt-5 px-10 py-2 text-lg font-bold md:py-4 md:text-xl lg:px-16 lg:text-3xl"
              onClick={() => {
                if (
                  degree.length > 0 ||
                  school.length > 0 ||
                  description.length > 0
                ) {
                  setIsOverlayOpen(true);
                } else {
                  handleClick();
                }
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
